import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import { Container } from '../../components/content';
import GenericTable from '../../components/tables/GenericTable';
import scheduledNotificationApi from '../../api/scheduled-notification-api';
import styles from '../../styles/mobileInfoPage.module.scss';
import vipStyles from '../../styles/vip.module.scss';
import dayjs from 'dayjs';

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

const DAYS_MAP = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];

const kvColumns = (header) => [
  { key: 'label', label: '' },
  { key: 'value', label: header },
];

export default function NotifikacijaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = location.state?.fromPage;
  const backPath = fromPage ? `/notifikacije?page=${fromPage}` : '/notifikacije';

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchNotification = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await scheduledNotificationApi.getById(id);
    if (success) {
      setNotification(data);
    } else {
      toast.error(message || 'Greška pri učitavanju notifikacije');
      navigate(backPath);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchNotification();
  }, [fetchNotification]);

  const formatScheduleValue = () => {
    if (!notification) return '-';
    if (notification.scheduleType === 'once') {
      return notification.scheduledAt
        ? dayjs(notification.scheduledAt).format('DD.MM.YYYY. HH:mm')
        : '-';
    }
    const time = `${String(notification.hour ?? 0).padStart(2, '0')}:${String(notification.minute ?? 0).padStart(2, '0')}`;
    const days =
      notification.daysOfWeek && notification.daysOfWeek.length > 0
        ? notification.daysOfWeek.map((d) => DAYS_MAP[d]).join(', ')
        : 'Svaki dan';
    return `${time} — ${days}`;
  };

  const titleData = LANG_SECTIONS.map(({ key, label }) => ({
    label,
    value: notification?.title?.[key] || '-',
  }));

  const bodyData = LANG_SECTIONS.map(({ key, label }) => ({
    label,
    value: notification?.body?.[key] || '-',
  }));

  const scheduleData = [
    { label: 'Tip', value: notification?.scheduleType === 'once' ? 'Jednokratno' : 'Ponavljajuće' },
    { label: 'Vreme slanja', value: formatScheduleValue() },
    ...(notification?.cronExpression ? [{ label: 'Cron izraz', value: notification.cronExpression }] : []),
  ];

  const statusMap = {
    active:   { bg: '#e6f4ea', color: '#1e7e34', label: 'Aktivno' },
    inactive: { bg: '#fdecea', color: '#c0392b', label: 'Neaktivno' },
    pending:  { bg: '#fff7e6', color: '#d46b08', label: 'Na čekanju' },
    sent:     { bg: '#e6f0ff', color: '#1d5cbf', label: 'Poslato' },
  };
  const currentStatus = statusMap[notification?.status] ?? { bg: '#f0f0f0', color: '#555', label: notification?.status ?? '-' };

  const metaData = [
    {
      label: 'Status',
      value: (
        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, backgroundColor: currentStatus.bg, color: currentStatus.color }}>
          {currentStatus.label}
        </span>
      ),
    },
    {
      label: 'Poslednje slanje',
      value: notification?.lastDispatchedAt
        ? dayjs(notification.lastDispatchedAt).format('DD.MM.YYYY. HH:mm')
        : 'Nije još poslato',
    },
    {
      label: 'Kreirano',
      value: notification?.createdAt
        ? dayjs(notification.createdAt).format('DD.MM.YYYY. HH:mm')
        : '-',
    },
    {
      label: 'Poslednja izmena',
      value: notification?.updatedAt
        ? dayjs(notification.updatedAt).format('DD.MM.YYYY. HH:mm')
        : '-',
    },
  ];

  if (loading) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <Link to={backPath} className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <div className={vipStyles.vip__inner}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p className="title">Detalji notifikacije</p>
            <Button
              className="btn-primary"
              type="primary"
              onClick={() => navigate(`/notifikacije/izmeni/${id}`, { state: { fromPage } })}
            >
              Izmeni
            </Button>
          </div>

          {/* Title + Body */}
          <div className={vipStyles.vip__grid}>
            <GenericTable
              data={titleData}
              columns={kvColumns('Naslov')}
              grayTheme={true}
            />
            <GenericTable
              data={bodyData}
              columns={kvColumns('Tekst')}
              grayTheme={true}
            />
          </div>

          {/* Meta + Schedule */}
          <div className={vipStyles.vip__grid}>
            <GenericTable
              data={metaData}
              columns={kvColumns('Stavka')}
              grayTheme={true}
            />
            <GenericTable
              data={scheduleData}
              columns={kvColumns('Raspored')}
              grayTheme={true}
            />
          </div>

        </div>
      </ContentLayout>
    </Container>
  );
}
